import React from 'react';
import { TableBody, TableRow, TableCell } from '@mui/material';
import { useSelector } from 'react-redux';
import { dateFormatDot } from 'Util/DateUtils';
import { phoneFormat } from 'Util/NumberUtils';
import OverflowTooltip from './OverflowTooltip';
import { COLUMNS, cellStyle } from './UpdateStaffTableStyles';
import BasicCheckbox from 'Components/Toggle/BasicCheckbox';
import emptyProfile from 'Assets/img/empty_profile@2x.png';
import DOMPurify from 'dompurify';
import IntlMessages from 'Util/IntlMessages';
import BasicSelectInput from 'Components/Input/BasicSelectInput';
import StaffRole from 'Constants/StaffRole';
import BasicTags from 'Components/common/BasicTags';

const UpdateStaffTableBody = ({ items, selectedStaffs, toggleCheck, nowFilter, onRoleChange, isSupervisor, originalStaffs }) => {
  const { locale } = useSelector(state => state.settings.locale);
  const { user } = useSelector(state => state.authUser);

  // 권한 옵션 생성
  const roleOptions = [
    { value: StaffRole.MEMBER, label: <IntlMessages id='staff.role.MEMBER' /> },
    { value: StaffRole.SUPERVISOR, label: <IntlMessages id='staff.role.SUPERVISOR' /> },
  ];

  // 정규표현식 특수문자를 이스케이프하는 함수
  const escapeRegExp = string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const changeWeightSelectedWord = (text, word) => {
    if (!word) return text;
    const escapedWord = escapeRegExp(word);
    const regex = new RegExp(`(${escapedWord})`, 'gi');
    return text.replace(regex, `<span style="font-weight: 700;">$1</span>`);
  };

  const getColumnStyle = column => {
    const baseStyle = {
      overflow: 'hidden !important',
    };

    // 체크박스 열인 경우
    if (column.key === 'select') {
      return {
        ...baseStyle,
        width: '40px !important',
        maxWidth: '40px !important',
        minWidth: '40px !important',
        padding: '0 !important',
        textAlign: 'center !important',
      };
    }

    if (column.width === 'flex') {
      return { ...baseStyle, flex: '1 1 0 !important' };
    }

    return {
      ...baseStyle,
      width: `${column.width} !important`,
      maxWidth: `${column.width} !important`,
      minWidth: `${column.width} !important`,
    };
  };

  const renderCellContent = (item, column) => {
    switch (column.key) {
      case 'select': {
        const isSelected = selectedStaffs.some(x => x._id === item._id);
        const isOriginalStaff = originalStaffs.some(x => x._id === item._id);
        const isCurrentUser = user.partner?.materialBuyer?.staffs?._id === item._id;
        const isDisabled = !isSupervisor && isOriginalStaff && !isCurrentUser;

        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <BasicCheckbox
              isChecked={isSelected}
              size='small'
              isDisabled={isDisabled}
              onClick={e => {
                e.stopPropagation(); // 행 클릭 이벤트와 중복 방지
                if (!isDisabled) {
                  toggleCheck(item);
                }
              }}
            />
          </div>
        );
      }
      case 'name':
        return (
          <div className='staff-list-body-content-list-table-cell-name' style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <img src={item.profileImage ? item.profileImage : emptyProfile} alt='empty-profile' style={{ width: '24px', height: '24px', minWidth: '24px', minHeight: '24px', flexShrink: 0 }} />
            <div
              className='staff-name-text'
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                maxWidth: '140px',
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(changeWeightSelectedWord(item.name, nowFilter?.name || '')) }}
            />
            {!item.active && <BasicTags type='secondary' color='red' size='x-small' textId='staff.create.basic.info.permission.restrict' />}
          </div>
        );
      case 'id':
        return item.id;
      case 'role': {
        const isSelected = selectedStaffs.some(x => x._id === item._id);
        const selectedStaff = selectedStaffs.find(x => x._id === item._id);

        // isSupervisor가 아닌 경우 권한 변경 비활성화
        const isRoleDisabled = !isSelected || !isSupervisor;

        // isSupervisor가 아닌 경우 MEMBER로 고정, 그 외에는 기존 로직 유지
        const currentRole = !isSupervisor ? StaffRole.MEMBER : selectedStaff?.role || item.role || StaffRole.MEMBER;

        return (
          <div onClick={e => e.stopPropagation()}>
            <BasicSelectInput
              placeholderId='staff.role.MEMBER'
              value={currentRole}
              onChange={e => onRoleChange && onRoleChange(item._id, e.target.value)}
              menuItems={roleOptions}
              disabled={isRoleDisabled}
              size='small'
            />
          </div>
        );
      }
      case 'department':
        return item.department?.name;
      case 'dateEntered':
        return dateFormatDot(item.dateEntered, locale);
      case 'phone':
        return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(changeWeightSelectedWord(phoneFormat(item.phone), nowFilter?.phone || '')) }} />;
      case 'birth':
        return dateFormatDot(item.birth, locale);
      default:
        return '';
    }
  };

  return (
    <TableBody>
      {items.map(item => {
        const isOriginalStaff = originalStaffs.some(x => x._id === item._id);
        const isCurrentUser = user.partner?.materialBuyer?.staffs?._id === item._id;
        const isDisabled = !isSupervisor && isOriginalStaff && !isCurrentUser;

        return (
          <TableRow
            key={item._id}
            className={`${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} no-drag ${!item.active && 'inactive'}`}
            onClick={() => {
              if (!isDisabled) {
                toggleCheck(item);
              }
            }}
          >
            {COLUMNS.map(column => (
              <TableCell key={column.key} sx={getColumnStyle(column)}>
                <OverflowTooltip title={renderCellContent(item, column)} placement='top'>
                  <div className='cursor-default' style={cellStyle}>
                    {renderCellContent(item, column)}
                  </div>
                </OverflowTooltip>
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default UpdateStaffTableBody;
